import React, { useState } from "react";
import { Box, Tab, Tabs, Button, Typography, Paper, Pagination, styled } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import RoutineStyle from "../../GlobalPolicies/RoutineUpload.module.css";
import { apiService } from "../../../api/apiService";
import ReuploadComponent from "./ReuploadComponent";
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';


interface ChangeManagementTabsProps {
  tabValue: string;
  handleTabChange: (event: React.SyntheticEvent, newValue: string) => void;
  tabStatuses: { label: string; value: 'All' | 'Pending' | 'Approved' | 'Reverted'; apiStatus: string | null }[];
  counts: { All: number; Pending: number; Approved: number; Reverted: number };
  verticalTabValue: string;
  verticalTabs: { label: string; value: string }[];
  isLoading: boolean;
  filteredRoutineData: any[];
  paginatedData: any[];
  routineData: any[];
  itemsPerPage: number;
  handleChangePage: (event: React.ChangeEvent<unknown>, value: number) => void;
  handleViewClick: (id: any, file_name: string, function_l1: any, created_at: any, updated_at: any, updated_by: any, status: any, reason_to_approve: any, reason_to_revert: any) => void;
  setOpenUpload: (open: boolean) => void;
  getFileImagePath: (doc_path: string) => string;
  handleVerticalTabChange?: (event: React.SyntheticEvent, newValue: string) => void;
  isMcrTab?: boolean;
  question?: any;
  onSuccess?: () => void;
  reuploadapiEndpoint?: string;
}

const StyledTableRow = styled(Box)<{ status: string }>(({ status }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '10px',
  boxShadow: `4px 0px 0px 0px ${status === 'Approved'
    ? '#058B2B'
    : status === 'Reverted'
      ? '#e53935'
      : status === 'Pending'
        ? '#FF5C00'
        : 'transparent'
    } inset`,
}));

const ChangeManagementTabs: React.FC<ChangeManagementTabsProps> = ({
  tabValue,
  handleTabChange,
  tabStatuses,
  counts,
  verticalTabValue,
  verticalTabs,
  isLoading,
  filteredRoutineData,
  paginatedData,
  routineData,
  itemsPerPage,
  handleChangePage,
  handleViewClick,
  setOpenUpload,
  getFileImagePath,
  handleVerticalTabChange,
  isMcrTab,
  question,
  onSuccess,
  reuploadapiEndpoint
}) => {
  const { instance } = useMsal();
  const management = localStorage.getItem('management') || '';
  const apiManagement = localStorage.getItem('apiManagement') || '';
  const isManagementMatching = management === apiManagement;
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  interface ApiResponse {
    status: string;
    data: any[];
  }

  const handleReupload = async (itemId: string, file: File): Promise<void> => {
    if (!reuploadapiEndpoint) return;

    const formData = new FormData();
    formData.append("file", file);


    try {
      const response = await apiService(`${reuploadapiEndpoint}/${itemId}`, "POST", formData, undefined, instance as PublicClientApplication);

      if (response.status !== "success") {
        throw new Error(response.message || "Failed to upload file");
      }

      // Refresh data after successful upload
      onSuccess?.();
    } catch (error) {
      console.error("Reupload error:", error);
      throw new Error(typeof error === 'string' ? error : "Failed to upload file");
    }
  };

  const getFileDescription = () => {
    switch (verticalTabValue) {
      case "a":
        return "Provide evidence of action plan closure, including risk mitigation activities and resolution tracking. Evidence should include reports from the GRM team showing actions taken and completed on time.";
      case "b":
        return "Evidence should include BCP planning documents showing process categorization and critical process";
      default:
        return "Upload file";
    }
  };

  const renderNoFilesContent = () => (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        There's no file uploaded right now
      </Typography>
      <Typography sx={{ mb: 2 }}>
        {getFileDescription()}
      </Typography>
      <Button
        variant="contained"
        className={RoutineStyle.border_btn}
        onClick={() => setOpenUpload(true)}
        disabled={!isManagementMatching}
      >
        Upload
      </Button>
    </Box>
  );


  console.log('@@@@@', paginatedData)

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <TabContext value={tabValue}>
        <Box sx={{ display: "flex", justifyContent: "space-between", borderColor: "divider" }}>
          <Box className={RoutineStyle.tabs_routine}>
            <TabList onChange={handleTabChange} aria-label="SIPOC Tabs">
              {tabStatuses.map((tab) => (
                <Tab
                  key={tab.value}
                  label={`${tab.label} (${counts[tab.value] || 0})`}
                  value={String(tabStatuses.indexOf(tab) + 1)}
                  sx={{ textTransform: 'none', fontWeight: '400' }}
                />
              ))}
            </TabList>
            <Button
              variant="contained"
              className={RoutineStyle.uploadBtn}
              onClick={() => setOpenUpload(true)}
              disabled={!isManagementMatching}
            >
              Upload
            </Button>
          </Box>
        </Box>

        <TabPanel value={tabValue}>
          <Box sx={{ display: "flex" }}>
            {isMcrTab && (
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={verticalTabValue}
                onChange={handleVerticalTabChange}
                aria-label="Vertical Tabs"
                sx={{ minWidth: "150px", textAlign: 'left' }}
                className="vertical_rTabs"
              >
                {verticalTabs.map((tab) => (
                  <Tab key={tab.value} label={tab.label} value={tab.value} />
                ))}
              </Tabs>
            )}

            {isLoading ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: "transparent",
                  height: "600px",
                  pt: 0,
                }}
              >
                <Typography className={RoutineStyle.noFileText}>
                  Fetching Files...
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ marginLeft: 2, flexGrow: 1 }}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: "transparent",
                    maxHeight: "600px",
                    pt: 0,
                  }}
                >
                  <Typography className={RoutineStyle.routineHeading}>
                    {verticalTabs.find((tab) => tab.value === verticalTabValue)?.label}
                  </Typography>

                  {verticalTabs.find((tab) => tab.value === verticalTabValue)?.label === 'Risk Management Tools' &&   (
                    <Typography sx={{ color: '#000', fontSize: '16px', pb: 2 }}>
                      Question : Action plans for the issues identified by the GRM team (Global Risk Management) in are closed on time
                    </Typography>
                  )}

                  <Typography sx={{ color: '#000', fontSize: '16px', pb: 2 }}>
                    {question}
                  </Typography>

                  {counts[tabStatuses[Number(tabValue) - 1].value] === 0 ? (
                    <Typography className={RoutineStyle.noFileText}>
                      File not found for the selected status.
                    </Typography>
                  ) : filteredRoutineData.length > 0 ? (
                    <div>
                      {paginatedData.map((item) => (
                        <StyledTableRow sx={{ mb: 2 }} status={item.status} key={item.id}>
                          <Paper className={RoutineStyle.bordercard} sx={{ width: '100%' }}>
                            <Box sx={{ display: "flex", gap: "50px", p: 2 }}>
                              <div style={{ display: "flex", textAlign: "left", gap: "5px", width: "40%" }}>
                                <div>
                                  <img src={getFileImagePath(item.doc_path)} alt="File" height={50} width={50} />
                                </div>
                                <div>
              
                                  <p>{item.file_name.split("_").slice(4).join("_")}</p>
                                  <h4>{item.file_size || "Unknown Size"}</h4>
                                </div>
                              </div>

                              {item.function_l1 && (
                                <div style={{ textAlign: "left", width: "20%" }}>
                                  <h4>Function L1</h4>
                                  <p>{item.function_l1}</p>
                                </div>
                              )}

                              <div style={{ textAlign: "left", width: "20%" }}>
                                <h4>Updated Date</h4>
                                <p>{new Date(item.created_at).toLocaleDateString()}</p>
                              </div>
                              <div style={{ textAlign: "left" }}>
                                <h4>Status</h4>
                                <p
                                  className={RoutineStyle.statuspending}
                                  style={{
                                    color:
                                      item.status === 'Reverted'
                                        ? '#F00'
                                        : item.status === 'Approved'
                                          ? '#058B2B'
                                          : item.status === 'Pending'
                                            ? '#FF5C00'
                                            : '#000',
                                  }}
                                >
                                  {item.status}
                                </p>
                              </div>
                            </Box>
                            <hr />

                            {(item.status === "Approved" || item.status === "Reverted") && (
                              <Box sx={{ padding: '10px' }}>
                                <Paper
                                  elevation={1}
                                  sx={{
                                    textAlign: 'left',
                                    borderRadius: '6px',
                                    border: `1px solid ${item.status === 'Reverted' ? 'red' : '#058B2B'}`,
                                    backgroundColor: item.status === 'Reverted' ? '#ffebeb' : '#e6ffed',
                                    padding: '10px',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: item.status === 'Reverted' ? 'red' : '#058B2B',
                                    }}
                                  >
                                    {item.status === "Approved" ? "Approved Reason" : "Reverted Reason"}
                                  </Typography>
                                  <Typography sx={{ paddingTop: '6px' }}>
                                    {item.status === "Approved" ? item.reason_to_approve : item.reason_to_revert}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            <Box sx={{ display: "flex", gap: 2, alignItems: 'center', justifyContent: "flex-end", padding: "10px", borderRadius: "9px" }}>
                              {item.status === "Reverted" && (
                                <ReuploadComponent
                                  itemId={item.id}
                                  onReupload={handleReupload}
                                  onSuccess={onSuccess}
                                />
                              )}
                              <Button
                                onClick={() => handleViewClick(
                                  item.id,
                                  item.file_name,
                                  item.function_l1,
                                  item.created_at,
                                  item.updated_at,
                                  item.updated_by,
                                  item.status,
                                  item.reason_to_approve,
                                  item.reason_to_revert
                                )}
                                className={RoutineStyle.black_btn}
                              >
                                View
                              </Button>
                            </Box>
                          </Paper>
                        </StyledTableRow>
                      ))}

                      {filteredRoutineData.length > 3 && (
                        <Pagination
                          color="primary"
                          count={Math.ceil(filteredRoutineData.length / itemsPerPage)}
                          onChange={handleChangePage}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                            "& .MuiPaginationItem-root": {
                              color: "black",
                            },
                            "& .MuiPaginationItem-ellipsis": {
                              color: "black",
                            },
                            "& .MuiPaginationItem-icon": {
                              color: "black",
                            },
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    renderNoFilesContent()
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default ChangeManagementTabs;